import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { MeProvider } from "@/lib/authz";
import { RoleCatalogProvider } from "@/lib/roles";
import Login from "@/pages/Login";
import Layout from "@/pages/Layout";
import ForcePasswordChange from "@/pages/ForcePasswordChange";
import Imports from "@/pages/Imports";
import SystemHealth from "@/pages/SystemHealth";
import Masters from "@/pages/Masters";
import Assignments from "@/pages/Assignments";
import Approvals from "@/pages/Approvals";
import Ledger from "@/pages/Ledger";
import CandidatePortal from "@/pages/CandidatePortal";
import PortalActivate from "@/pages/PortalActivate";
import Dashboard from "@/pages/Dashboard";
import Employees from "@/pages/Employees";
import Candidates from "@/pages/Candidates";
import CandidateDetail from "@/pages/CandidateDetail";
import DailyActivity from "@/pages/DailyActivity";
import JobWorkspace from "@/pages/JobWorkspace";
import Hotlist from "@/pages/Hotlist";
import ColdEmails from "@/pages/ColdEmails";
import Interviews from "@/pages/Interviews";
import Offers from "@/pages/Offers";
import Performance from "@/pages/Performance";
import Analytics from "@/pages/Analytics";
import Reports from "@/pages/Reports";
import Evaluations from "@/pages/Evaluations";
import Incentives from "@/pages/Incentives";
import ChangeRequestQueue from "@/pages/ChangeRequestQueue";
import NewLeads from "@/pages/NewLeads";
import JobLibrary from "@/pages/JobLibrary";
import JobDetail from "@/pages/JobDetail";
import Targets from "@/pages/Targets";
import AuditLog from "@/pages/AuditLog";
import Settings from "@/pages/Settings";
import "@/App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForcePasswordChange />;
  return children;
}

function RoleGuard({ roles, children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

const LEADER_ROLES = ["admin", "team_lead"];
const LEAD_ROLES = ["admin", "team_lead", "sdl", "senior_executive", "hr"];
const ADMIN_ONLY = ["admin"];
const APPROVAL_ROLES = ["admin", "team_lead", "sdl", "hr", "executive", "sde", "senior_executive"];
const FINANCE_ROLES = ["admin", "hr"];
const EVAL_ROLES = ["admin", "team_lead", "sdl"];
const ASSIGN_ROLES = ["admin", "team_lead", "hr", "sdl", "senior_executive"];

function App() {
  return (
    <AuthProvider>
      <MeProvider>
      <BrowserRouter>
        <Toaster position="bottom-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/portal" element={<CandidatePortal />} />
          <Route path="/candidate-portal" element={<CandidatePortal />} />
          <Route path="/portal/activate" element={<PortalActivate />} />
          <Route path="/" element={<Protected><RoleCatalogProvider><Layout /></RoleCatalogProvider></Protected>}>
            <Route index element={<Hotlist />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="candidates" element={<Candidates />} />
            <Route path="candidates/:id" element={<CandidateDetail />} />
            <Route path="hotlist" element={<Hotlist />} />
            <Route path="job-workspace" element={<JobWorkspace />} />
            <Route path="daily-activity" element={<Navigate to="/job-workspace" replace />} />
            <Route path="daily-activity-legacy" element={<DailyActivity />} />
            <Route path="evaluations" element={<RoleGuard roles={EVAL_ROLES}><Evaluations /></RoleGuard>} />
            <Route path="change-requests" element={<RoleGuard roles={EVAL_ROLES}><ChangeRequestQueue /></RoleGuard>} />
            <Route path="new-leads" element={<RoleGuard roles={ASSIGN_ROLES}><NewLeads /></RoleGuard>} />
            <Route path="jobs" element={<JobLibrary />} />
            <Route path="jobs/:id" element={<JobDetail />} />
            <Route path="cold-emails" element={<ColdEmails />} />
            <Route path="interviews" element={<Interviews />} />
            <Route path="offers" element={<Offers />} />
            <Route path="employees" element={<RoleGuard roles={LEADER_ROLES}><Employees /></RoleGuard>} />
            <Route path="masters" element={<RoleGuard roles={ADMIN_ONLY}><Masters /></RoleGuard>} />
            <Route path="imports" element={<RoleGuard roles={ADMIN_ONLY}><Imports /></RoleGuard>} />
            <Route path="system-health" element={<RoleGuard roles={ADMIN_ONLY}><SystemHealth /></RoleGuard>} />
            <Route path="assignments" element={<RoleGuard roles={ASSIGN_ROLES}><Assignments /></RoleGuard>} />
            <Route path="analytics" element={<RoleGuard roles={LEADER_ROLES}><Analytics /></RoleGuard>} />
            <Route path="performance" element={<RoleGuard roles={LEAD_ROLES}><Performance /></RoleGuard>} />
            <Route path="incentives" element={<RoleGuard roles={ADMIN_ONLY}><Incentives /></RoleGuard>} />
            <Route path="approvals" element={<RoleGuard roles={APPROVAL_ROLES}><Approvals /></RoleGuard>} />
            <Route path="ledger" element={<RoleGuard roles={FINANCE_ROLES}><Ledger /></RoleGuard>} />
            <Route path="targets" element={<RoleGuard roles={LEADER_ROLES}><Targets /></RoleGuard>} />
            <Route path="reports" element={<RoleGuard roles={LEAD_ROLES}><Reports /></RoleGuard>} />
            <Route path="audit" element={<RoleGuard roles={LEADER_ROLES}><AuditLog /></RoleGuard>} />
            <Route path="settings" element={<RoleGuard roles={ADMIN_ONLY}><Settings /></RoleGuard>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </MeProvider>
    </AuthProvider>
  );
}

export default App;
